import { useCallback, useEffect, useMemo, useState } from "react";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  Legend,
} from "recharts";
import { format, isAfter, isBefore, isToday } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import {
  CalendarDays,
  Plus,
  ListPlus,
  Layers,
  Building2,
  CircleDot,
  AlertCircle,
  PencilLine,
  Trash2,
  ExternalLink,
  Filter,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import type { Booking } from "@/types";
import ExternalBookingForm from "./ExternalBookingForm";
import ExternalBookingsBulkAdd from "./ExternalBookingsBulkAdd";
import {
  ExternalBookingRow,
  ExternalSchedulingMode,
  ExternalBookingStatus,
  EXTERNAL_EVENT_COLOR,
  PLATFORM_EVENT_COLOR,
  UnifiedCalendarEvent,
  type UnifiedVendorEvent,
  buildUnifiedEvents,
  dayKey,
  externalDays,
  formatTimeRange,
  groupUnifiedEventsByDay,
  safeDate,
} from "./types";
import {
  CONFIDENCE_BADGE_CLASS,
  RISK_LABEL_BADGE_CLASS,
  aggregateRiskCounts,
  computeDayLoadFlags,
  computeRiskLabel,
  evaluateRowFlags,
  formatRiskReasonLocalized,
  getConfidenceCopyLocalized,
  getConfidenceLevel,
  getCountsSummaryLocalized,
  getDayFlagsBannerLocalized,
  getSuggestedActionLocalized,
  trackSchedulerActionShown,
  trackSchedulerSummaryViewed,
  type ClientFallbackInput,
  type DayLoadFlags,
  type RowRiskFlags,
} from "@/lib/scheduler";
import {
  useVendorLoadSnapshot,
  useVendorRiskSummary,
} from "@/hooks/scheduler";
import { Lock, ShieldAlert, AlertTriangle, ListChecks } from "lucide-react";

interface Props {
  companyId: string;
  bookings: Booking[];
  services?: Array<{ id: string; name: string }>;
}

type SourceFilter = "all" | "platform" | "external";

type DayCapacityFact = {
  date: string;
  totalCapacity: number;
  blocked: boolean;
};

export default function ExternalBookingsTracker({ companyId, bookings, services }: Props) {
  const { toast } = useToast();
  const { t, tx, lang } = useI18n();
  const dateLocale = lang === "en" ? enUS : itLocale;
  const schedulingModeLabel = (mode: ExternalSchedulingMode) => {
    switch (mode) {
      case "exact": return t("vendor.tracker.mode.exact");
      case "window": return t("vendor.tracker.mode.window");
      case "date_only": return t("vendor.tracker.mode.date_only");
      case "flexible": return t("vendor.tracker.mode.flexible");
    }
  };
  const externalStatusLabel = (status: ExternalBookingStatus) => {
    switch (status) {
      case "planned": return t("vendor.tracker.status.planned");
      case "in_progress": return t("vendor.tracker.status.in_progress");
      case "completed": return t("vendor.tracker.status.completed");
      case "cancelled": return t("vendor.tracker.status.cancelled");
    }
  };

  const [externalRows, setExternalRows] = useState<ExternalBookingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [singleOpen, setSingleOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<ExternalBookingRow | null>(null);
  const [detailEvent, setDetailEvent] = useState<UnifiedCalendarEvent | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [capacityByDate, setCapacityByDate] = useState<Record<string, DayCapacityFact>>({});

  const loadExternal = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabaseUntyped
        .from("external_bookings")
        .select("*")
        .eq("company_id", companyId)
        .order("start_at", { ascending: true, nullsFirst: false });
      if (error) throw error;
      setExternalRows((data || []) as ExternalBookingRow[]);
    } catch (err) {
      const message = err instanceof Error ? err.message : t("vendor.tracker.retry");
      toast({ title: t("vendor.tracker.load_error"), description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadExternal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Load vendor capacity for the days touched by the unified load (Sprint 2.5).
  // We look 60 days ahead to cover the typical scheduling horizon.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!companyId) return;
      try {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const end = new Date(start.getTime() + 60 * 24 * 60 * 60 * 1000);
        const startIso = format(start, "yyyy-MM-dd");
        const endIso = format(end, "yyyy-MM-dd");

        type RawSlot = { date: string; slot: string; capacity: number | null; blocked: boolean | null };
        type RawDay = { date: string; capacity: number | null; blocked: boolean | null };

        const { data: slotsRows } = await supabaseUntyped
          .from("vendor_availability_slots")
          .select("date, slot, capacity, blocked")
          .eq("company_id", companyId)
          .gte("date", startIso)
          .lte("date", endIso);

        const map: Record<string, DayCapacityFact> = {};
        if (Array.isArray(slotsRows) && slotsRows.length > 0) {
          for (const r of slotsRows as RawSlot[]) {
            const date = String(r.date);
            const cap = Number(r.capacity ?? 0);
            const blocked = !!r.blocked;
            const prev = map[date] ?? { date, totalCapacity: 0, blocked: true };
            map[date] = {
              date,
              totalCapacity: prev.totalCapacity + (blocked ? 0 : Math.max(0, cap)),
              blocked: prev.blocked && blocked,
            };
          }
        } else {
          // Fallback to legacy per-day vendor_availability if slots are not configured.
          const { data: dayRows } = await supabaseUntyped
            .from("vendor_availability")
            .select("date, capacity, blocked")
            .eq("company_id", companyId)
            .gte("date", startIso)
            .lte("date", endIso);
          for (const r of (dayRows ?? []) as RawDay[]) {
            const date = String(r.date);
            const blocked = !!r.blocked;
            map[date] = {
              date,
              totalCapacity: blocked ? 0 : Math.max(0, Number(r.capacity ?? 0)),
              blocked,
            };
          }
        }

        if (active) setCapacityByDate(map);
      } catch {
        if (active) setCapacityByDate({});
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId]);

  // Build the unified event list
  const events = useMemo<UnifiedCalendarEvent[]>(() => {
    const list: UnifiedCalendarEvent[] = [];

    // Platform bookings
    for (const b of bookings) {
      const day = safeDate(b.scheduled_date);
      if (!day) continue;
      const meta = b as Booking & Partial<{ customer_name: string; booking_number: string; service_name: string }>;
      list.push({
        id: `platform-${b.id}`,
        source: "platform",
        day,
        timeLabel: typeof b.scheduled_time === "string" ? b.scheduled_time.slice(0, 5) : "",
        title: meta.customer_name || meta.booking_number || t("vendor.tracker.booking_fallback"),
        subtitle: meta.service_name || "",
        color: PLATFORM_EVENT_COLOR,
        modeLabel: t("vendor.tracker.kpi.platform"),
        statusLabel: capitalize(String(b.status || "").replace(/_/g, " ")),
        raw: b,
      });
    }

    // External bookings (window-aware: appears on every day inside the window).
    // Source-color (green) is the visual rule for external events; status nuance is
    // expressed by the text label / badge to keep the calendar legend crystal clear.
    for (const ext of externalRows) {
      const days = externalDays(ext);
      const time = formatTimeRange(ext, t);
      const dotColor = ext.status === "cancelled" ? "#94a3b8" : EXTERNAL_EVENT_COLOR;
      if (days.length === 0) {
        list.push({
          id: `external-${ext.id}`,
          source: "external",
          day: new Date(0),
          timeLabel: t("vendor.tracker.mode.flexible"),
          title: ext.title || ext.address || ext.guest_name || t("vendor.tracker.external_booking"),
          subtitle: ext.service_label || "",
          color: dotColor,
          modeLabel: schedulingModeLabel(ext.scheduling_mode),
          statusLabel: externalStatusLabel(ext.status),
          raw: ext,
        });
        continue;
      }
      for (const d of days) {
        list.push({
          id: `external-${ext.id}-${d.toISOString()}`,
          source: "external",
          day: d,
          timeLabel: time,
          title: ext.title || ext.address || ext.guest_name || t("vendor.tracker.external_booking"),
          subtitle: ext.service_label || "",
          color: dotColor,
          modeLabel: schedulingModeLabel(ext.scheduling_mode),
          statusLabel: externalStatusLabel(ext.status),
          raw: ext,
        });
      }
    }

    return list;
  }, [bookings, externalRows, t]);

  /** KPI e grafici day-based: stesso perimetro del load (`buildUnifiedEvents`) — niente esterni `cancelled`. */
  const eventsForAggregates = useMemo(
    () =>
      events.filter((e) => {
        if (e.source !== "external") return true;
        return (e.raw as ExternalBookingRow).status !== "cancelled";
      }),
    [events]
  );

  /**
   * Booking-level aggregator: una entry per booking (non per (booking, giorno)).
   *
   * Il calendario espande gli external `window` / `date_only` su ogni giorno
   * della finestra — corretto per i pallini sul calendario ma sbagliato per
   * KPI tipo "Totale", "Esterni" e per i grafici per servizio: una finestra di
   * 3 giorni si conterebbe 3 volte. Usiamo questo array per tutto cio' che
   * deve contare "quanti booking" e non "quante giornate-evento".
   */
  type AggregateBooking =
    | { source: "platform"; id: string; firstDay: Date | null; raw: Booking }
    | { source: "external"; id: string; firstDay: Date | null; raw: ExternalBookingRow };
  const bookingsForAggregates = useMemo<AggregateBooking[]>(() => {
    const list: AggregateBooking[] = [];
    for (const b of bookings) {
      const day = safeDate(b.scheduled_date);
      if (!day) continue;
      list.push({ source: "platform", id: String(b.id), firstDay: day, raw: b });
    }
    for (const ext of externalRows) {
      if (ext.status === "cancelled") continue;
      const days = externalDays(ext);
      list.push({ source: "external", id: ext.id, firstDay: days[0] ?? null, raw: ext });
    }
    return list;
  }, [bookings, externalRows]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (sourceFilter !== "all" && e.source !== sourceFilter) return false;
      if (statusFilter !== "all") {
        const status = e.source === "external" ? (e.raw as ExternalBookingRow).status : (e.raw as Booking).status;
        if (String(status) !== statusFilter) return false;
      }
      return true;
    });
  }, [events, sourceFilter, statusFilter]);

  // KPIs
  const kpis = useMemo(() => {
    const now = new Date();
    const in7 = new Date();
    in7.setDate(now.getDate() + 7);

    const platformBookings = bookingsForAggregates.filter((b) => b.source === "platform");
    const externalBookings = bookingsForAggregates.filter((b) => b.source === "external");

    // "Oggi" e "Prossimi 7 giorni" usano le occorrenze per giorno (per
    // catturare un window che cade in mezzo al periodo) ma deduplicano per
    // booking id, cosi' un window di 3 giorni che cade tutto nei prossimi 7
    // conta come 1 e non come 3.
    const eventBookingKey = (e: UnifiedCalendarEvent) =>
      e.source === "platform"
        ? `p-${(e.raw as Booking).id}`
        : `e-${(e.raw as ExternalBookingRow).id}`;
    const todaySet = new Set<string>();
    const next7Set = new Set<string>();
    for (const e of eventsForAggregates) {
      if (!e.day || e.day.getTime() === 0) continue;
      if (isToday(e.day)) todaySet.add(eventBookingKey(e));
      if (isToday(e.day) || (isAfter(e.day, now) && isBefore(e.day, in7))) {
        next7Set.add(eventBookingKey(e));
      }
    }

    return {
      totalAll: platformBookings.length + externalBookings.length,
      platform: platformBookings.length,
      external: externalBookings.length,
      next7: next7Set.size,
      todayCount: todaySet.size,
    };
  }, [bookingsForAggregates, eventsForAggregates]);

  // Charts
  const sourceMix = useMemo(
    () => [
      { name: t("vendor.tracker.kpi.platform"), value: kpis.platform, color: PLATFORM_EVENT_COLOR },
      { name: t("vendor.tracker.kpi.external"), value: kpis.external, color: EXTERNAL_EVENT_COLOR },
    ],
    [kpis, t]
  );

  // Two separate breakdowns: platform `service_name` (real catalog services)
  // and external `service_label` (free text — vendors use it for turnover
  // typology, e.g. "Turnover lungo", "Pulizia same-day"). Mixing them in a
  // single chart conflates two unrelated axes, so we surface them in tabs.
  // Counts are per-booking (not per (booking, giorno)) — vedi
  // `bookingsForAggregates`.
  const platformByService = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of bookingsForAggregates) {
      if (b.source !== "platform") continue;
      const raw = b.raw as Booking & { service_name?: string };
      const name = (raw.service_name || "").trim() || t("vendor.tracker.other");
      map.set(name, (map.get(name) ?? 0) + 1);
    }
    return Array.from(map, ([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [bookingsForAggregates]);

  const externalByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of bookingsForAggregates) {
      if (b.source !== "external") continue;
      const name = ((b.raw as ExternalBookingRow).service_label || "").trim() || t("vendor.tracker.no_label");
      map.set(name, (map.get(name) ?? 0) + 1);
    }
    return Array.from(map, ([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [bookingsForAggregates]);

  // Calendar
  const eventsByDay = useMemo(() => {
    const map = new Map<string, UnifiedCalendarEvent[]>();
    for (const e of filteredEvents) {
      if (!e.day || e.day.getTime() === 0) continue;
      const k = format(e.day, "yyyy-MM-dd");
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    }
    return map;
  }, [filteredEvents]);

  const selectedDayEvents = useMemo(() => {
    return (eventsByDay.get(format(selectedDate, "yyyy-MM-dd")) || []).slice().sort((a, b) =>
      (a.timeLabel || "").localeCompare(b.timeLabel || "")
    );
  }, [eventsByDay, selectedDate]);

  const flexibleExternals = useMemo(
    () => externalRows.filter((r) => r.scheduling_mode === "flexible" && r.status !== "cancelled"),
    [externalRows]
  );

  /**
   * External con range gia' dichiarato ma non ancora ancorati a un orario /
   * giorno preciso:
   *   - `window`    → range checkout/check-in con orari (es. 10/05 11:00 →
   *                   12/05 15:00)
   *   - `date_only` → range di sole date (es. 10/05 → 12/05)
   * Entrambi vivono nella tab "Da pianificare" perche' il vendor di solito
   * vuole convertirli in `exact` quando ha deciso il dettaglio (test EBT-019).
   * Sono trattati allo stesso modo lato workflow ma con badge / formato
   * differente sulla card per non confondere chi guarda la lista.
   */
  const rangeExternals = useMemo(
    () =>
      externalRows.filter(
        (r) =>
          (r.scheduling_mode === "window" || r.scheduling_mode === "date_only") &&
          r.status !== "cancelled"
      ),
    [externalRows]
  );

  const pendingPlanCount = flexibleExternals.length + rangeExternals.length;

  // ---------------------------------------------------------------------
  // Sprint 2.5 — Unified vendor load model
  // ---------------------------------------------------------------------
  //
  // Why client computation still lives here in Sprint 5:
  //   * The "Da gestire adesso" / "Finestre strette" sections render the
  //     INDIVIDUAL events (urgent + tight rows). The server engine returns
  //     only the aggregates, so we keep `flagsByKey` to identify which rows
  //     to show.
  //   * `flagsByKey` + `dayFlags` also feed `useVendorRiskSummary`'s
  //     `clientFallback` so the banner degrades gracefully when the engine
  //     is unreachable or the killswitch is on.
  //
  // The aggregate banner (`riskLabel`, `suggestedAction`, reasons,
  // confidence) and the calendar modifiers come from the server engine
  // (`useVendorRiskSummary` + `useVendorLoadSnapshot`) below.

  /** All vendor events (platform + external, expanded per day). */
  const unifiedEvents = useMemo<UnifiedVendorEvent[]>(
    () => buildUnifiedEvents({ companyId, bookings, externalRows }),
    [companyId, bookings, externalRows]
  );

  const unifiedByDay = useMemo(() => groupUnifiedEventsByDay(unifiedEvents), [unifiedEvents]);

  /** Per-row risk flags, keyed by `UnifiedVendorEvent.key`. */
  const flagsByKey = useMemo(() => {
    const map = new Map<string, RowRiskFlags>();
    for (const e of unifiedEvents) {
      map.set(
        e.key,
        evaluateRowFlags({
          source: e.source,
          window_start_at: e.window_start_at,
          window_end_at: e.window_end_at,
          scheduled_date: e.source === "platform" ? (e.raw as Booking).scheduled_date ?? null : null,
          scheduled_time: e.source === "platform" ? (e.raw as Booking).scheduled_time ?? null : null,
          checkout_at: e.checkout_at,
          checkin_at: e.checkin_at,
          scheduling_precision: e.scheduling_precision,
          start_at:
            e.source === "external" ? (e.raw as ExternalBookingRow).start_at ?? null : null,
        })
      );
    }
    return map;
  }, [unifiedEvents]);

  /**
   * Per-day flags (only for days that actually have load).
   * `dayCapacity = 0 && !blocked` is treated as "unknown / not yet declared":
   * we keep `closed_day_with_load = false` to avoid noisy banners for vendors
   * that haven't published their availability yet.
   */
  const dayFlags = useMemo<Array<{ date: string } & DayLoadFlags>>(() => {
    const out: Array<{ date: string } & DayLoadFlags> = [];
    for (const [date, evs] of unifiedByDay.entries()) {
      const cap = capacityByDate[date];
      const sameDayTurnoverRows = evs.filter((e) => flagsByKey.get(e.key)?.same_day_turnover).length;
      const flags = computeDayLoadFlags({
        load: evs.length,
        capacity: cap?.totalCapacity ?? 0,
        // Treat "no capacity record" as "unknown" rather than "blocked".
        blocked: cap?.blocked === true,
        sameDayTurnoverRows,
      });
      const hasAny =
        flags.over_capacity_day ||
        flags.near_capacity_day ||
        flags.closed_day_with_load ||
        flags.same_day_turnover_cluster;
      if (hasAny) out.push({ date, ...flags });
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
  }, [unifiedByDay, capacityByDate, flagsByKey]);

  const counts = useMemo(
    () => aggregateRiskCounts(Array.from(flagsByKey.values())),
    [flagsByKey]
  );
  // Local-only baseline (used as `clientFallback` for the server engine call
  // and as the visible value when the engine has no data yet).
  const localRiskLabel = useMemo(() => computeRiskLabel(counts, dayFlags), [counts, dayFlags]);
  const localDayBanner = useMemo(() => getDayFlagsBannerLocalized(dayFlags, { t, tx }), [dayFlags, t, tx]);

  // ---------------------------------------------------------------------
  // Sprint 5 — server engine integration
  // ---------------------------------------------------------------------
  // Declared before the calendar-modifier memos below: those memos read
  // `loadSnapshot.data`, so keeping these hooks above avoids a TDZ crash
  // in production builds where the minifier inlines bindings.
  // Window pinned to today + 60d to mirror the capacity prefetch above. The
  // local row/day flags become `clientFallback` so the banner stays identical
  // when the RPC is unreachable or the killswitch is on.
  const [serverWindow] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(start.getTime() + 60 * 24 * 60 * 60 * 1000);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
    };
  });

  const buildTrackerFallback = useCallback((): ClientFallbackInput => ({
    rowFlags: Array.from(flagsByKey.values()),
    dayFlags: dayFlags.map((d) => ({
      ...d,
      vendor_load: (unifiedByDay.get(d.date) ?? []).length,
      capacity_published: capacityByDate[d.date]?.totalCapacity ?? 0,
    })),
  }), [flagsByKey, dayFlags, unifiedByDay, capacityByDate]);

  const serverSummary = useVendorRiskSummary({
    companyId,
    startDate: serverWindow.startDate,
    endDate: serverWindow.endDate,
    clientFallback: buildTrackerFallback,
  });

  const loadSnapshot = useVendorLoadSnapshot({
    companyId,
    startDate: serverWindow.startDate,
    endDate: serverWindow.endDate,
  });

  // Calendar modifiers: prefer the load snapshot (richer + authoritative),
  // fall back to the locally-computed dayFlags when the snapshot RPC is
  // unreachable or the killswitch is on.
  const overCapacityDates = useMemo(() => {
    if (loadSnapshot.data) {
      return new Set(loadSnapshot.data.filter((d) => d.over_capacity_day).map((d) => d.day));
    }
    return new Set(dayFlags.filter((d) => d.over_capacity_day).map((d) => d.date));
  }, [loadSnapshot.data, dayFlags]);
  const nearCapacityDates = useMemo(() => {
    if (loadSnapshot.data) {
      return new Set(loadSnapshot.data.filter((d) => d.near_capacity_day).map((d) => d.day));
    }
    return new Set(dayFlags.filter((d) => d.near_capacity_day).map((d) => d.date));
  }, [loadSnapshot.data, dayFlags]);
  const closedWithLoadDates = useMemo(() => {
    if (loadSnapshot.data) {
      return new Set(loadSnapshot.data.filter((d) => d.closed_day_with_load).map((d) => d.day));
    }
    return new Set(dayFlags.filter((d) => d.closed_day_with_load).map((d) => d.date));
  }, [loadSnapshot.data, dayFlags]);

  // Authoritative aggregates: prefer the server engine, fall back to local.
  const riskLabel = serverSummary.data?.risk_label ?? localRiskLabel;
  const reasonChips = serverSummary.data?.risk_reasons ?? [];
  // Server `suggested_action` is Italian-only in SQL; derive copy from counts + day_flags via i18n.
  const suggestedAction = useMemo(() => {
    const displayCounts = serverSummary.data?.counts ?? counts;
    const displayDayFlags = serverSummary.data?.day_flags ?? dayFlags;
    return getSuggestedActionLocalized(displayCounts, { t, tx }, displayDayFlags);
  }, [serverSummary.data, counts, dayFlags, t, tx]);
  const confidenceLevel = serverSummary.data
    ? getConfidenceLevel(serverSummary.data.confidence_score)
    : null;

  // Server day_flags include `vendor_load` / `capacity_published`, perfect for
  // the calendar banner copy. We reuse the same banner formatter on both
  // shapes so microcopy stays centralised.
  const dayBanner = useMemo(() => {
    const serverDayFlags = serverSummary.data?.day_flags;
    if (serverDayFlags && serverDayFlags.length > 0) {
      return getDayFlagsBannerLocalized(serverDayFlags, { t, tx });
    }
    return localDayBanner;
  }, [serverSummary.data, localDayBanner, t, tx]);

  /** "Da gestire adesso": urgent rows + flexible externals (already surfaced separately, but counted here). */
  const urgentEvents = useMemo(
    () =>
      unifiedEvents
        .filter((e) => flagsByKey.get(e.key)?.urgent)
        .sort((a, b) => (a.window_end_at || "").localeCompare(b.window_end_at || "")),
    [unifiedEvents, flagsByKey]
  );

  const tightWindowEvents = useMemo(
    () =>
      unifiedEvents
        .filter((e) => flagsByKey.get(e.key)?.tight_window)
        .sort((a, b) => (a.window_start_at || "").localeCompare(b.window_start_at || "")),
    [unifiedEvents, flagsByKey]
  );

  const sameDayClusterDates = useMemo(
    () => dayFlags.filter((d) => d.same_day_turnover_cluster).map((d) => d.date),
    [dayFlags]
  );

  // Analytics: log scheduler summary view once the engine resolves a value.
  // Both `server` and `client_fallback` sources go through the same shim so
  // the dashboard (Sprint 8) can compare adoption and outage rates.
  useEffect(() => {
    if (!companyId) return;
    if (!serverSummary.data || !serverSummary.source) return;
    trackSchedulerSummaryViewed({
      surface: "external_bookings_tracker",
      scope: "vendor_window",
      source: serverSummary.source,
      companyId,
      startDate: serverWindow.startDate,
      endDate: serverWindow.endDate,
      summary: serverSummary.data,
    });
    trackSchedulerActionShown({
      surface: "external_bookings_tracker",
      scope: "vendor_window",
      actionId: "tracker_suggested_action",
      actionLabel: serverSummary.data.suggested_action,
      source: serverSummary.source,
      companyId,
      riskLabel: serverSummary.data.risk_label,
      reasonCodes: serverSummary.data.risk_reasons.map((r) => r.code),
    });
  }, [
    companyId,
    serverWindow.startDate,
    serverWindow.endDate,
    serverSummary.data,
    serverSummary.source,
  ]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("vendor.tracker.delete_confirm"))) return;
    try {
      const { error } = await supabaseUntyped.from("external_bookings").delete().eq("id", id);
      if (error) throw error;
      setExternalRows((rows) => rows.filter((r) => r.id !== id));
      setDetailEvent(null);
      toast({ title: t("vendor.tracker.deleted"), description: t("vendor.tracker.deleted_desc") });
    } catch (err) {
      const message = err instanceof Error ? err.message : t("vendor.tracker.delete_failed");
      toast({ title: t("vendor.tracker.error"), description: message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <Card className="border-gray-200 shadow-card">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl font-semibold text-slate-950">
              <Layers className="h-5 w-5 text-primary" />
              {t("vendor.tracker.title")}
            </CardTitle>
            <CardDescription className="text-slate-600 mt-1">
              {t("vendor.tracker.desc")}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              <ListPlus className="h-4 w-4 mr-2" /> {t("vendor.tracker.bulk_add")}
            </Button>
            <Button onClick={() => { setEditing(null); setSingleOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" /> {t("vendor.tracker.new_external")}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label={t("vendor.tracker.kpi.total")} value={kpis.totalAll} icon={<Layers className="h-4 w-4" />} />
            <Kpi
              label={t("vendor.tracker.kpi.platform")}
              value={kpis.platform}
              icon={<Building2 className="h-4 w-4" />}
              accent={PLATFORM_EVENT_COLOR}
            />
            <Kpi
              label={t("vendor.tracker.kpi.external")}
              value={kpis.external}
              icon={<ExternalLink className="h-4 w-4" />}
              accent={EXTERNAL_EVENT_COLOR}
            />
            <Kpi label={t("vendor.tracker.kpi.today")} value={kpis.todayCount} icon={<CircleDot className="h-4 w-4" />} />
            <Kpi label={t("vendor.tracker.kpi.next7")} value={kpis.next7} icon={<CalendarDays className="h-4 w-4" />} />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">{t("vendor.tracker.chart_mix")}</CardTitle>
              </CardHeader>
              <CardContent>
                {kpis.totalAll === 0 ? (
                  <div className="text-sm text-slate-500 h-[220px] flex items-center justify-center">
                    {t('vendor.tracker.no_data')}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={sourceMix}
                        innerRadius={45}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        stroke="#fff"
                        strokeWidth={2}
                      >
                        {sourceMix.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <ReTooltip formatter={(v: number, n: string) => [`${v}`, n]} />
                      <Legend verticalAlign="bottom" />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t("vendor.tracker.chart_services")}</CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  {t("vendor.tracker.chart_services_desc")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="platform" className="w-full">
                  <TabsList className="bg-slate-100/70 mb-3">
                    <TabsTrigger value="platform">
                      {t("vendor.tracker.tab_halomora")}
                      <span className="ml-1.5 text-[11px] text-slate-500">
                        ({platformByService.reduce((acc, r) => acc + r.value, 0)})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="external">
                      {t("vendor.tracker.tab_external")}
                      <span className="ml-1.5 text-[11px] text-slate-500">
                        ({externalByCategory.reduce((acc, r) => acc + r.value, 0)})
                      </span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="platform" className="mt-0">
                    {platformByService.length === 0 ? (
                      <div className="text-sm text-slate-500 h-[180px] flex items-center justify-center">
                        {t('vendor.tracker.no_platform_bookings')}
                      </div>
                    ) : (
                      <ServiceBarList
                        data={platformByService}
                        color={PLATFORM_EVENT_COLOR}
                        unitLabel={t("vendor.tracker.unit_booking")}
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="external" className="mt-0">
                    {externalByCategory.length === 0 ? (
                      <div className="text-sm text-slate-500 h-[180px] flex items-center justify-center">
                        {t('vendor.tracker.no_external')}
                      </div>
                    ) : (
                      <ServiceBarList
                        data={externalByCategory}
                        color={EXTERNAL_EVENT_COLOR}
                        unitLabel={t("vendor.tracker.unit_booking")}
                      />
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Scheduler summary (Sprint 2.5 — wired to server engine in Sprint 5) */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent-green-500/5 shadow-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-base text-slate-950">
                <ShieldAlert className="h-4 w-4 text-primary" /> {t("vendor.tracker.scheduler_title")}
              </CardTitle>
              <CardDescription className="text-slate-600 mt-1">
                {t("vendor.tracker.scheduler_desc")}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={RISK_LABEL_BADGE_CLASS[riskLabel]}>
                {riskLabel}
              </Badge>
              {confidenceLevel ? (
                <Badge
                  variant="outline"
                  className={CONFIDENCE_BADGE_CLASS[confidenceLevel]}
                  title={getConfidenceCopyLocalized(confidenceLevel, { t, tx })}
                >
                  {tx("vendor.tracker.confidence", { level: t(`scheduler.confidence_level.${confidenceLevel}`) })}
                </Badge>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-slate-700">
            {tx("vendor.tracker.scheduler_signals", { summary: getCountsSummaryLocalized(counts, { t, tx }) })}
            {counts.same_day > 0 ? ` • ${counts.same_day} same-day turnover` : ""}
          </div>
          {reasonChips.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {reasonChips.map((r) => (
                <Badge
                  key={`${r.code}-${r.scope}`}
                  variant="outline"
                  className="border-slate-300 text-slate-700 text-[10px]"
                >
                  {formatRiskReasonLocalized(r, { t, tx })}
                </Badge>
              ))}
            </div>
          ) : null}
          <div className="text-sm text-slate-800">{suggestedAction}</div>
          {dayBanner ? (
            <div className="flex gap-2 items-start text-sm rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="break-words">{dayBanner}</div>
            </div>
          ) : null}
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <Lock className="h-3 w-3" />
            <span>{t("vendor.tracker.privacy_note")}</span>
          </div>
          <div className="text-[10px] text-slate-500">
            {tx("vendor.tracker.source_label", {
              source:
                serverSummary.source === "server"
                  ? t("vendor.tracker.source_server")
                  : serverSummary.source === "client_fallback"
                    ? t("vendor.tracker.source_fallback")
                    : t("vendor.tracker.source_local"),
              extra: serverSummary.isLoading ? ` • ${t("vendor.tracker.updating")}` : "",
            })}
          </div>
        </CardContent>
      </Card>

      {/* Da gestire adesso (Sprint 2.5) */}
      {(urgentEvents.length > 0 || flexibleExternals.length > 0) && (
        <Card className="border-red-200 bg-red-50/40 shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-red-900">
              <ListChecks className="h-4 w-4" /> {tx("vendor.tracker.urgent_title", { count: urgentEvents.length + flexibleExternals.length })}
            </CardTitle>
            <CardDescription className="text-red-800">
              {t("vendor.tracker.urgent_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {urgentEvents.slice(0, 6).map((e) => (
              <div
                key={e.key}
                className="rounded-xl border border-red-200 bg-white p-3 flex items-start gap-3"
              >
                <span
                  className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: e.source === "platform" ? PLATFORM_EVENT_COLOR : EXTERNAL_EVENT_COLOR }}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">
                      {e.source === "platform" ? t("vendor.tracker.badge_platform") : t("vendor.tracker.badge_external")}
                    </Badge>
                    <Badge variant="outline" className="border-red-300 text-red-700 text-[10px]">
                      {t("vendor.tracker.badge_24h")}
                    </Badge>
                    <span className="text-sm font-medium text-slate-900 break-words">
                      {e.source === "platform"
                        ? (() => {
                            const meta = e.raw as Booking & Partial<{ customer_name: string; booking_number: string }>;
                            return meta.customer_name || meta.booking_number || t("vendor.tracker.booking_fallback");
                          })()
                        : (e.raw as ExternalBookingRow).title || (e.raw as ExternalBookingRow).address || (e.raw as ExternalBookingRow).guest_name || t("vendor.tracker.external_booking")}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {format(e.day, "d MMM", { locale: dateLocale })}
                    {e.window_end_at
                      ? ` • ${tx("vendor.tracker.closes", { date: format(new Date(e.window_end_at), "d MMM HH:mm", { locale: dateLocale }) })}`
                      : ""}
                  </div>
                </div>
              </div>
            ))}
            {flexibleExternals.slice(0, 4).map((row) => (
              <div
                key={`flex-${row.id}`}
                className="rounded-xl border border-red-200 bg-white p-3 flex items-start gap-3"
              >
                <AlertCircle className="h-4 w-4 text-red-600 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{t("vendor.tracker.badge_external")}</Badge>
                    <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px]">
                      {t("vendor.tracker.badge_flexible")}
                    </Badge>
                    <span className="text-sm font-medium text-slate-900 break-words">
                      {row.title || row.address || row.guest_name || t("vendor.tracker.external_booking")}
                    </span>
                  </div>
                  {row.notes ? <div className="text-xs text-slate-600 mt-1 break-words">{row.notes}</div> : null}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(row);
                    setSingleOpen(true);
                  }}
                >
                  <PencilLine className="h-3 w-3 mr-1" /> {t("vendor.tracker.plan")}
                </Button>
              </div>
            ))}
            {urgentEvents.length + flexibleExternals.length > 10 && (
              <div className="text-xs text-slate-600">
                {tx("vendor.tracker.more_rows", { n: urgentEvents.length + flexibleExternals.length - 10 })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Finestre strette / cluster same-day (Sprint 2.5) */}
      {(tightWindowEvents.length > 0 || sameDayClusterDates.length > 0) && (
        <Card className="border-amber-200 bg-amber-50/40 shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-amber-900">
              <AlertTriangle className="h-4 w-4" /> {t("vendor.tracker.tight_title")}
            </CardTitle>
            <CardDescription className="text-amber-800">
              {t("vendor.tracker.tight_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {tightWindowEvents.slice(0, 6).map((e) => (
              <div
                key={`tight-${e.key}`}
                className="rounded-xl border border-amber-200 bg-white p-3 flex items-start gap-3"
              >
                <span
                  className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: e.source === "platform" ? PLATFORM_EVENT_COLOR : EXTERNAL_EVENT_COLOR }}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">
                      {e.source === "platform" ? t("vendor.tracker.badge_platform") : t("vendor.tracker.badge_external")}
                    </Badge>
                    <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px]">
                      {t("vendor.tracker.badge_tight")}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {e.window_start_at && e.window_end_at
                      ? `${format(new Date(e.window_start_at), "d MMM HH:mm", { locale: dateLocale })} → ${format(new Date(e.window_end_at), "HH:mm", { locale: dateLocale })}`
                      : format(e.day, "d MMM", { locale: dateLocale })}
                  </div>
                </div>
              </div>
            ))}
            {sameDayClusterDates.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-white p-3 text-sm text-slate-700">
                <span className="font-medium">{t("vendor.tracker.cluster_same_day")}</span>{" "}
                {sameDayClusterDates
                  .slice(0, 5)
                  .map((d) => format(new Date(d + "T00:00:00"), "d MMM", { locale: dateLocale }))
                  .join(", ")}
                {sameDayClusterDates.length > 5 ? ` (+${sameDayClusterDates.length - 5})` : ""}
                {" "}{t("vendor.tracker.cluster_hint")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 shadow-soft">
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <Filter className="h-4 w-4" /> {t("vendor.tracker.filters")}
        </div>
        <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as SourceFilter)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vendor.tracker.filter_all_sources")}</SelectItem>
            <SelectItem value="platform">{t("vendor.tracker.filter_platform_only")}</SelectItem>
            <SelectItem value="external">{t("vendor.tracker.filter_external_only")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vendor.tracker.filter_all_status")}</SelectItem>
            <SelectItem value="planned">{t("vendor.tracker.status.planned")}</SelectItem>
            <SelectItem value="pending">{t("vendor.tracker.status.pending")}</SelectItem>
            <SelectItem value="confirmed">{t("vendor.tracker.status.confirmed")}</SelectItem>
            <SelectItem value="in_progress">{t("vendor.tracker.status.in_progress")}</SelectItem>
            <SelectItem value="completed">{t("vendor.tracker.status.completed")}</SelectItem>
            <SelectItem value="cancelled">{t("vendor.tracker.status.cancelled")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex flex-wrap gap-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PLATFORM_EVENT_COLOR }} />
            {t("vendor.tracker.kpi.platform")}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: EXTERNAL_EVENT_COLOR }} />
            {t("vendor.tracker.kpi.external")}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-md ring-2 ring-red-400" />
            {t("vendor.tracker.legend_over")}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-md ring-2 ring-amber-300" />
            {t("vendor.tracker.legend_near")}
          </span>
        </div>
      </div>

      {/* Calendar / List tabs */}
      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="bg-slate-100/70">
          <TabsTrigger value="calendar"><CalendarDays className="h-4 w-4 mr-2" /> {t("vendor.tracker.tab_calendar")}</TabsTrigger>
          <TabsTrigger value="list"><Layers className="h-4 w-4 mr-2" /> {t("vendor.tracker.tab_list")}</TabsTrigger>
          {pendingPlanCount > 0 && (
            <TabsTrigger value="todo"><AlertCircle className="h-4 w-4 mr-2" /> {tx("vendor.tracker.tab_todo", { count: pendingPlanCount })}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="calendar" className="space-y-4">
          <Card className="border-gray-200 shadow-card">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 min-w-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                    locale={dateLocale}
                    className="rounded-2xl border border-gray-200 shadow-sm w-full"
                    modifiers={{
                      hasPlatform: (date) =>
                        (eventsByDay.get(format(date, "yyyy-MM-dd")) || []).some((e) => e.source === "platform"),
                      hasExternal: (date) =>
                        (eventsByDay.get(format(date, "yyyy-MM-dd")) || []).some((e) => e.source === "external"),
                      hasBoth: (date) => {
                        const list = eventsByDay.get(format(date, "yyyy-MM-dd")) || [];
                        return list.some((e) => e.source === "platform") && list.some((e) => e.source === "external");
                      },
                      overCapacity: (date) => overCapacityDates.has(format(date, "yyyy-MM-dd")),
                      nearCapacity: (date) => nearCapacityDates.has(format(date, "yyyy-MM-dd")),
                      closedWithLoad: (date) => closedWithLoadDates.has(format(date, "yyyy-MM-dd")),
                    }}
                    modifiersClassNames={{
                      hasPlatform:
                        "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-[6px] after:h-1.5 after:w-1.5 after:rounded-full after:bg-[#1d4ed8]",
                      hasExternal:
                        "relative before:content-[''] before:absolute before:bottom-1 before:left-1/2 before:translate-x-[2px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#10b981]",
                      hasBoth: "font-semibold",
                      overCapacity: "ring-2 ring-red-400 ring-offset-1 rounded-md",
                      closedWithLoad: "ring-2 ring-red-500 ring-offset-1 rounded-md",
                      nearCapacity: "ring-2 ring-amber-300 ring-offset-1 rounded-md",
                    }}
                  />
                </div>
                <div className="space-y-3 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">
                      {format(selectedDate, "EEEE d MMMM", { locale: dateLocale })}
                    </h3>
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                      {tx("vendor.tracker.events", { count: selectedDayEvents.length })}
                    </Badge>
                  </div>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {selectedDayEvents.length === 0 ? (
                      <div className="text-sm text-slate-500 text-center py-8">
                        {t('vendor.tracker.no_day_bookings')}
                      </div>
                    ) : (
                      selectedDayEvents.map((e) => (
                        <button
                          key={e.id}
                          onClick={() => setDetailEvent(e)}
                          className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 hover:shadow-soft transition-shadow"
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: e.color }}
                              aria-hidden
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm text-slate-900 break-words">{e.title}</span>
                                <Badge variant="outline" className="text-[10px]">
                                  {e.source === "platform" ? t("vendor.tracker.badge_platform") : t("vendor.tracker.badge_external")}
                                </Badge>
                              </div>
                              {e.subtitle && (
                                <div className="text-xs text-slate-500 break-words">{e.subtitle}</div>
                              )}
                              <div className="text-xs text-slate-600 mt-1">
                                {e.timeLabel || "—"} · {e.statusLabel}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list" className="space-y-3">
          {filteredEvents.length === 0 ? (
            <Card className="border-dashed border-slate-300">
              <CardContent className="py-12 text-center text-slate-500">
                {t('vendor.tracker.no_filtered_events')}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredEvents
                .slice()
                .sort((a, b) => (a.day?.getTime() ?? 0) - (b.day?.getTime() ?? 0))
                .map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setDetailEvent(e)}
                    className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 hover:shadow-soft transition-shadow flex items-start gap-3"
                  >
                    <span
                      className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: e.color }}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 break-words">{e.title}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {e.source === "platform" ? t("vendor.tracker.badge_platform") : t("vendor.tracker.badge_external")}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700">
                          {e.statusLabel}
                        </Badge>
                      </div>
                      {e.subtitle && <div className="text-xs text-slate-500 break-words">{e.subtitle}</div>}
                      <div className="text-xs text-slate-600 mt-1">
                        {e.day && e.day.getTime() !== 0 ? format(e.day, "d MMM yyyy", { locale: dateLocale }) : t("vendor.tracker.no_date")}
                        {" · "}
                        {e.timeLabel || "—"}
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </TabsContent>

        {pendingPlanCount > 0 && (
          <TabsContent value="todo" className="space-y-3">
            {flexibleExternals.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="text-base text-amber-900">
                    {tx("vendor.tracker.flexible_title", { count: flexibleExternals.length })}
                  </CardTitle>
                  <CardDescription className="text-amber-800">
                    {t("vendor.tracker.flexible_desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {flexibleExternals.map((row) => (
                    <div
                      key={row.id}
                      className="rounded-xl border border-amber-200 bg-white p-3 flex items-start gap-3"
                    >
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 break-words">
                          {row.title || row.address || row.guest_name || t("vendor.tracker.external_booking")}
                        </div>
                        {row.service_label && (
                          <div className="text-xs text-slate-500 break-words">{row.service_label}</div>
                        )}
                        {row.notes && (
                          <div className="text-xs text-slate-600 mt-1 break-words">{row.notes}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(row);
                          setSingleOpen(true);
                        }}
                      >
                        <PencilLine className="h-3 w-3 mr-1" /> {t("vendor.tracker.plan")}
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {rangeExternals.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/40">
                <CardHeader>
                  <CardTitle className="text-base text-blue-900">
                    {tx("vendor.tracker.range_title", { count: rangeExternals.length })}
                  </CardTitle>
                  <CardDescription className="text-blue-800">
                    {t("vendor.tracker.range_desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {rangeExternals.map((row) => (
                    <div
                      key={row.id}
                      className="rounded-xl border border-blue-200 bg-white p-3 flex items-start gap-3"
                    >
                      <CalendarDays className="h-4 w-4 text-blue-600 mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 break-words">
                            {row.title || row.address || row.guest_name || t("vendor.tracker.external_booking")}
                          </span>
                          <Badge variant="outline" className="border-blue-300 text-blue-700 text-[10px]">
                            {schedulingModeLabel(row.scheduling_mode)}
                          </Badge>
                        </div>
                        {row.service_label && (
                          <div className="text-xs text-slate-500 break-words">{row.service_label}</div>
                        )}
                        <div className="text-xs text-slate-600 mt-1 break-words">
                          {t("vendor.tracker.range_label")}: {renderWhen(row, { t, lang, dateLocale })}
                        </div>
                        {row.notes && (
                          <div className="text-xs text-slate-600 mt-1 break-words">{row.notes}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(row);
                          setSingleOpen(true);
                        }}
                      >
                        <PencilLine className="h-3 w-3 mr-1" /> {t("vendor.tracker.plan")}
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Details sheet */}
      <Sheet open={!!detailEvent} onOpenChange={(open) => !open && setDetailEvent(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detailEvent && (
            <>
              <SheetHeader>
                <SheetTitle className="break-words">{detailEvent.title}</SheetTitle>
                <SheetDescription>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <Badge variant="outline">{detailEvent.source === "platform" ? t("vendor.tracker.badge_platform") : t("vendor.tracker.badge_external")}</Badge>
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">{detailEvent.statusLabel}</Badge>
                    <Badge variant="outline">{detailEvent.modeLabel}</Badge>
                  </div>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-3 text-sm">
                {detailEvent.source === "platform" ? (
                  <PlatformDetails booking={detailEvent.raw as Booking} />
                ) : (
                  <ExternalDetails row={detailEvent.raw as ExternalBookingRow} />
                )}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {detailEvent.source === "external" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditing(detailEvent.raw as ExternalBookingRow);
                        setDetailEvent(null);
                        setSingleOpen(true);
                      }}
                    >
                      <PencilLine className="h-4 w-4 mr-2" /> {t("vendor.tracker.edit")}
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete((detailEvent.raw as ExternalBookingRow).id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> {t("vendor.tracker.delete")}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add / Edit dialogs */}
      <ExternalBookingForm
        open={singleOpen}
        onOpenChange={(o) => {
          setSingleOpen(o);
          if (!o) setEditing(null);
        }}
        companyId={companyId}
        services={services}
        initial={editing}
        onSaved={(row) => {
          setExternalRows((rows) => {
            const filtered = rows.filter((r) => r.id !== row.id);
            return [...filtered, row].sort((a, b) => {
              const ad = safeDate(a.start_at)?.getTime() ?? Number.MAX_SAFE_INTEGER;
              const bd = safeDate(b.start_at)?.getTime() ?? Number.MAX_SAFE_INTEGER;
              return ad - bd;
            });
          });
        }}
      />
      <ExternalBookingsBulkAdd
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        companyId={companyId}
        defaultServiceLabel="Pulizia"
        onImported={(rows) => setExternalRows((prev) => [...prev, ...rows])}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-soft">
      <div className="flex items-center justify-between text-slate-500 text-xs">
        <span>{label}</span>
        <span style={{ color: accent }}>{icon}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-950 mt-1">{value}</div>
    </div>
  );
}

/** Platform booking fields not yet declared in the canonical `Booking` type. */
type PlatformBookingDetails = Booking & Partial<{
  service_name: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  address: string;
  booking_number: string;
}>;

function PlatformDetails({ booking }: { booking: Booking }) {
  const { t, lang } = useI18n();
  const dateLocale = lang === "en" ? enUS : itLocale;
  const b = booking as PlatformBookingDetails;
  return (
    <div className="space-y-2">
      <Row label={t("vendor.tracker.detail.service")} value={b.service_name} />
      <Row label={t("vendor.tracker.detail.customer")} value={b.customer_name} />
      <Row label={t("vendor.tracker.detail.phone")} value={b.customer_phone} />
      <Row label={t("vendor.tracker.detail.email")} value={b.customer_email} />
      <Row label={t("vendor.tracker.detail.address")} value={b.address} />
      <Row label={t("vendor.tracker.detail.date")} value={format(new Date(booking.scheduled_date), "d MMMM yyyy", { locale: dateLocale })} />
      <Row label={t("vendor.tracker.detail.time")} value={booking.scheduled_time} />
      <Row label={t("vendor.tracker.detail.amount")} value={formatCurrency(Number(booking.total_amount || 0))} />
      <Row label={t("vendor.tracker.detail.number")} value={b.booking_number} />
      {booking.notes && <Row label={t("vendor.tracker.detail.notes")} value={booking.notes} />}
    </div>
  );
}

function ExternalDetails({ row }: { row: ExternalBookingRow }) {
  const { t, lang } = useI18n();
  const dateLocale = lang === "en" ? enUS : itLocale;
  return (
    <div className="space-y-2">
      <Row label={t("vendor.tracker.detail.label")} value={row.title} />
      <Row label={t("vendor.tracker.detail.address")} value={row.address} />
      <Row label={t("vendor.tracker.detail.customer")} value={row.guest_name} />
      <Row label={t("vendor.tracker.detail.phone")} value={row.guest_phone} />
      <Row label={t("vendor.tracker.detail.email")} value={row.guest_email} />
      <Row label={t("vendor.tracker.detail.service")} value={row.service_label} />
      <Row label={t("vendor.tracker.detail.when")} value={renderWhen(row, { t, lang, dateLocale })} />
      {row.duration_minutes ? <Row label={t("vendor.tracker.detail.duration")} value={`${row.duration_minutes} min`} /> : null}
      {row.guests ? <Row label={t("vendor.tracker.detail.guests")} value={String(row.guests)} /> : null}
      {row.has_pets ? <Row label={t("vendor.tracker.detail.pets")} value={t("vendor.tracker.detail.yes")} /> : null}
      {row.has_kids ? <Row label={t("vendor.tracker.detail.kids")} value={t("vendor.tracker.detail.yes")} /> : null}
      {row.amount != null ? (
        <Row label={t("vendor.tracker.detail.amount")} value={formatCurrency(Number(row.amount))} />
      ) : null}
      {row.notes && <Row label={t("vendor.tracker.detail.notes")} value={row.notes} />}
    </div>
  );
}

function renderWhen(
  row: ExternalBookingRow,
  opts: { t: (key: string) => string; lang: string; dateLocale: typeof enUS }
): string {
  const { t, lang, dateLocale } = opts;
  const s = safeDate(row.start_at);
  const e = safeDate(row.end_at);
  switch (row.scheduling_mode) {
    case "exact":
      return s
        ? format(s, lang === "en" ? "d MMM yyyy, HH:mm" : "d MMM yyyy 'alle' HH:mm", { locale: dateLocale })
        : "—";
    case "window":
      return s && e
        ? `${format(s, "d MMM HH:mm", { locale: dateLocale })} → ${format(e, "d MMM HH:mm", { locale: dateLocale })}`
        : "—";
    case "date_only":
      return s && e
        ? `${format(s, "d MMM", { locale: dateLocale })} → ${format(e, "d MMM", { locale: dateLocale })}`
        : "—";
    case "flexible":
      return t("vendor.tracker.flexible_pending");
  }
}

function Row({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 min-w-[110px]">{label}:</span>
      <span className="text-slate-900 break-words">{value}</span>
    </div>
  );
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * "Top servizi" bar list. Reso in HTML/CSS (non Recharts) perche' la card
 * spesso vive in colonna stretta e i tick label SVG di Recharts vengono
 * troncati in modo poco prevedibile (vedi Sprint 5 feedback). Qui i nomi
 * vanno a capo se serve, e il tooltip nativo (`title`) mostra il valore
 * completo al passaggio del mouse.
 */
function ServiceBarList({
  data,
  color,
  unitLabel,
}: {
  data: Array<{ name: string; value: number }>;
  color: string;
  unitLabel: string;
}) {
  const max = data.reduce((acc, d) => (d.value > acc ? d.value : acc), 0) || 1;
  return (
    <ul className="space-y-3">
      {data.map((row) => {
        const pct = Math.max(2, Math.round((row.value / max) * 100));
        return (
          <li key={row.name} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3">
              <span
                className="text-xs text-slate-700 break-words leading-snug"
                title={row.name}
              >
                {row.name}
              </span>
              <span className="text-xs font-semibold text-slate-900 tabular-nums shrink-0">
                {row.value}
              </span>
            </div>
            <div
              className="h-2 bg-slate-100 rounded-full overflow-hidden"
              role="meter"
              aria-valuenow={row.value}
              aria-valuemin={0}
              aria-valuemax={max}
              aria-label={`${row.name}: ${row.value} ${unitLabel}`}
            >
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
